import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
    baseURL: `${API_URL}/api`,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Athletes API
export const athletesApi = {
    getAll: (params) => api.get('/athletes', { params }),
    getById: (id) => api.get(`/athletes/${id}`),
    getMyProfile: () => api.get('/athletes/me/profile'),
    createProfile: (data) => api.post('/athletes', data),
    updateProfile: (data) => api.put('/athletes/me/profile', data),
};

// Tournaments API
export const tournamentsApi = {
    getAll: (params) => api.get('/tournaments', { params }),
    getById: (id) => api.get(`/tournaments/${id}`),
    create: (data) => api.post('/tournaments', data),
    register: (id) => api.post(`/tournaments/${id}/register`),
    registerDetailed: (id, data) => api.post(`/tournaments/${id}/register-detailed`, data),
    registerTeam: (id, data) => api.post(`/tournaments/${id}/register-team`, data),
    isTeamSport: (id) => api.get(`/tournaments/${id}/is-team-sport`),
    getRegistrationFee: (id, numDisciplines) => api.get(`/tournaments/${id}/registration-fee?num_disciplines=${numDisciplines}`),
    getMatches: (id) => api.get(`/tournaments/${id}/matches`),
    // My Registrations
    getMyRegistrations: () => api.get('/my-registrations'),
    cancelRegistration: (tournamentId) => api.delete(`/my-registrations/${tournamentId}`),
};

// Cart API
export const cartApi = {
    getCart: () => api.get('/cart'),
    removeItem: (registrationId) => api.delete(`/cart/${registrationId}`),
    checkout: (paymentData) => api.post('/cart/checkout', paymentData || {}),
};

// Leaderboards API
export const leaderboardsApi = {
    getBySport: (sport, params) => api.get(`/leaderboards/${sport}`, { params }),
    getSchoolRankings: (params) => api.get('/school-rankings', { params }),
};

// AI API
export const aiApi = {
    generateSchedule: (data) => api.post('/ai/generate-schedule', data),
    detectTalent: (data) => api.post('/ai/talent-detection', data),
    predictMatch: (data) => api.post('/ai/match-prediction', data),
};

// Stats API
export const statsApi = {
    getOverview: () => api.get('/stats/overview'),
    getSports: () => api.get('/sports'),
    getAthletes: () => api.get('/stats/athletes'),
    getTournaments: () => api.get('/stats/tournaments'),
    getVerified: () => api.get('/stats/verified'),
    getActive: () => api.get('/stats/active'),
};

// Admin Registrations API
export const adminRegistrationsApi = {
    getAll: (params) => api.get('/admin/registrations', { params }),
    getStats: () => api.get('/admin/registrations/stats'),
    updateStatus: (id, status) => api.put(`/admin/registrations/${id}/status?status=${status}`),
    delete: (id) => api.delete(`/admin/registrations/${id}`),
};

// Sports API
export const sportsApi = {
    getAll: () => api.get('/sports'),
    getDetails: (sportName) => api.get(`/sports/${sportName}`),
};

// Video Analysis API
export const videoAnalysisApi = {
    uploadVideo: (formData) => api.post('/video-analysis/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
    }),
    getStatus: (videoId) => api.get(`/video-analysis/status/${videoId}`),
    getResult: (videoId) => api.get(`/video-analysis/result/${videoId}`),
    getAthleteAnalyses: (athleteId) => api.get(`/video-analysis/athlete/${athleteId}`),
    getAllAnalyses: (params) => api.get('/video-analysis/all', { params }),
    getSupportedSports: () => api.get('/video-analysis/sports'),
    getStats: () => api.get('/video-analysis/stats'),
    getLeaderboard: (params) => api.get('/video-analysis/leaderboard', { params }),
    deleteAnalysis: (videoId) => api.delete(`/video-analysis/${videoId}`),
    grantAccess: (videoId, userIds) => api.post(`/video-analysis/grant-access/${videoId}`, userIds),
    revokeAccess: (videoId, userIds) => api.post(`/video-analysis/revoke-access/${videoId}`, userIds),
    addScoutNotes: (videoId, notes, scoutId) => api.post(`/video-analysis/scout-notes/${videoId}`, null, { 
        params: { notes, scout_id: scoutId } 
    }),
    getWatchlist: (userId) => api.get(`/video-analysis/watchlist/${userId}`),
    addToWatchlist: (userId, athleteId, videoId) => api.post('/video-analysis/watchlist/add', null, {
        params: { user_id: userId, athlete_id: athleteId, video_id: videoId }
    }),
    removeFromWatchlist: (userId, athleteId, videoId) => api.delete('/video-analysis/watchlist/remove', {
        params: { user_id: userId, athlete_id: athleteId, video_id: videoId }
    }),
};

// Chunked Upload API
export const chunkedUploadApi = {
    init: (formData) => api.post('/video-upload/init', formData),
    uploadChunk: (uploadId, formData) => api.post(`/video-upload/chunk/${uploadId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
    }),
    finalize: (uploadId) => api.post(`/video-upload/finalize/${uploadId}`),
    getStatus: (uploadId) => api.get(`/video-upload/status/${uploadId}`),
    cancel: (uploadId) => api.delete(`/video-upload/cancel/${uploadId}`),
};

// Highlights API
export const highlightsApi = {
    generate: (data) => api.post('/highlights/generate', data),
    getForVideo: (videoId) => api.get(`/highlights/video/${videoId}`),
    stream: (highlightId) => `${API_URL}/api/highlights/stream/${highlightId}`,
    publish: (highlightId) => api.post(`/highlights/publish/${highlightId}`),
    coachReview: (highlightId, data) => api.post(`/highlights/coach-review/${highlightId}`, data),
    getForAthlete: (athleteId) => api.get(`/highlights/athlete/${athleteId}`),
    delete: (highlightId) => api.delete(`/highlights/${highlightId}`),
    getShared: (shareToken) => api.get(`/highlights/share/${shareToken}`),
};

// PDF Reports API
export const reportsApi = {
    generatePdf: (videoId) => `${API_URL}/api/reports/generate/${videoId}`,
    checkAvailability: (videoId) => api.get(`/reports/check/${videoId}`),
};

// Drill Portfolio API
export const portfolioApi = {
    create: (data) => api.post('/drill-portfolio/create', data),
    get: (portfolioId) => api.get(`/drill-portfolio/${portfolioId}`),
    getForUser: (userId) => api.get(`/drill-portfolio/user/${userId}`),
    getForAthlete: (athleteId, sport) => api.get(`/drill-portfolio/athlete/${athleteId}`, { params: sport ? { sport } : {} }),
    uploadDrill: (portfolioId, formData) => api.post(`/drill-portfolio/${portfolioId}/upload-drill`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000
    }),
    delete: (portfolioId) => api.delete(`/drill-portfolio/${portfolioId}`),
    analyzeAll: (portfolioId) => api.post(`/drill-portfolio/${portfolioId}/analyze-all`),
};

// Seed data
export const seedDemoData = () => api.post('/seed-demo-data');

// Rankings API
export const rankingsApi = {
    getConfig: () => api.get('/rankings/config'),
    getStats: () => api.get('/rankings/stats/overview'),
    getSeasons: () => api.get('/rankings/seasons'),
    getActiveSeason: () => api.get('/rankings/seasons/active'),
    getAthleteRankings: (params) => api.get('/rankings/athletes', { params }),
    getAthleteRankingDetail: (id, params) => api.get(`/rankings/athletes/${id}`, { params }),
    getSchoolRankings: (params) => api.get('/rankings/schools', { params }),
    getSportRankings: (sport, params) => api.get(`/rankings/sport/${sport}`, { params }),
    getTopRated: (params) => api.get('/rankings/ratings/top', { params }),
    getAthleteRatings: (params) => api.get('/rankings/ratings/athletes', { params }),
    getRatingHistory: (id, params) => api.get(`/rankings/ratings/history/${id}`, { params }),
    getTalentPool: (params) => api.get('/rankings/talent-pool', { params }),
    getEligibleAthletes: (params) => api.get('/rankings/sponsorship/eligible-athletes', { params }),
    getSponsorshipTiers: () => api.get('/rankings/sponsorship/tiers'),
    // Leaderboards
    getNationalLeaderboard: (params) => api.get('/rankings/leaderboards/national', { params }),
    getSchoolLeaderboard: (params) => api.get('/rankings/leaderboards/schools', { params }),
    getTopRatedLeaderboard: (params) => api.get('/rankings/leaderboards/top-rated', { params }),
    // My Rankings (for athlete dashboard)
    getMyRankings: (athleteId) => api.get('/rankings/athletes', { params: { athlete_id: athleteId } }),
};

// Corporate Sponsorship API
export const sponsorshipApi = {
    // Corporate auth
    corporateRegister: (data) => api.post('/sponsorship/corporates/register', data),
    corporateLogin: (data) => api.post('/sponsorship/corporates/login', data),
    // Corporate dashboard
    getCorporateProfile: () => api.get('/sponsorship/corporates/me'),
    updateCorporateProfile: (data) => api.put('/sponsorship/corporates/me', data),
    // Packages
    getPackages: () => api.get('/sponsorship/packages'),
    purchasePackage: (data) => api.post('/sponsorship/packages/purchase', data),
    // Sport allocations
    getAllocations: () => api.get('/sponsorship/allocations/me'),
    allocateBudget: (data) => api.post('/sponsorship/allocations', data),
    // Athlete discovery
    discoverAthletes: (params) => api.get('/sponsorship/discover-athletes', { params }),
    getAthleteProfile: (id) => api.get(`/sponsorship/athlete-profile/${id}`),
    // Offers & contracts
    sendOffer: (data) => api.post('/sponsorship/offers', data),
    getMyOffers: (params) => api.get('/sponsorship/offers/me', { params }),
    respondToOffer: (id, data) => api.post(`/sponsorship/offers/${id}/respond`, data),
    getContracts: (params) => api.get('/sponsorship/contracts', { params }),
    // Currencies
    getCurrencies: () => api.get('/sponsorship/currencies'),
    // Admin
    adminGetCorporates: (params) => api.get('/sponsorship/admin/corporates', { params }),
    adminReviewCorporate: (id, data) => api.post(`/sponsorship/admin/corporates/${id}/review`, data),
    adminGetPackages: () => api.get('/sponsorship/admin/packages'),
    adminCreatePackage: (data) => api.post('/sponsorship/admin/packages', data),
    adminUpdatePackage: (id, data) => api.put(`/sponsorship/admin/packages/${id}`, data),
    adminGetAnalytics: () => api.get('/sponsorship/admin/analytics'),
    adminGetSportReport: () => api.get('/sponsorship/admin/sport-report'),
};

export default api;

// Biomechanics API (Phase 3)
export const biomechanicsApi = {
    analyze: (data) => api.post('/biomechanics/analyze', data),
    getResult: (analysisId) => api.get(`/biomechanics/result/${analysisId}`),
    getKeypoints: (analysisId, startFrame, endFrame) => api.get(`/biomechanics/keypoints/${analysisId}`, { params: { start_frame: startFrame, end_frame: endFrame } }),
    getForVideo: (videoId) => api.get(`/biomechanics/video/${videoId}`),
    getSportsConfig: () => api.get('/biomechanics/sports-config'),
    getAthleteAnalyses: (athleteId) => api.get(`/biomechanics/athlete/${athleteId}`),
    getProgress: (athleteId, sport) => api.get(`/biomechanics/progress/${athleteId}`, { params: sport ? { sport } : {} }),
};

// Player Tracking API (Phase 4)
export const playerTrackingApi = {
    analyze: (data) => api.post('/player-tracking/analyze', data),
    getResult: (trackingId) => api.get(`/player-tracking/result/${trackingId}`),
    getDetections: (trackingId, startFrame, endFrame) => api.get(`/player-tracking/detections/${trackingId}`, { params: { start_frame: startFrame, end_frame: endFrame } }),
    getHeatmap: (trackingId, playerIndex, resolution) => api.get(`/player-tracking/heatmap/${trackingId}`, { params: { player_index: playerIndex, resolution } }),
    getForVideo: (videoId) => api.get(`/player-tracking/video/${videoId}`),
    getPitchDimensions: () => api.get('/player-tracking/pitch-dimensions'),
};

// Demo Mode API
export const demoApi = {
    seed: () => api.post('/demo/seed'),
    status: () => api.get('/demo/status'),
    clear: () => api.delete('/demo/clear'),
};

// Drill Library & Trials API (Phase 5)
export const drillLibraryApi = {
    getLibrary: (sport) => api.get('/drills/library', { params: sport ? { sport } : {} }),
    getDrill: (drillId) => api.get(`/drills/library/${drillId}`),
    getNextGenScore: (athleteId) => api.get(`/drills/nextgen-score/${athleteId}`),
    getBenchmarks: (athleteId, sport) => api.get(`/drills/benchmark/${athleteId}`, { params: sport ? { sport } : {} }),
    createTrial: (data) => api.post('/drills/trials/create', data),
    listTrials: (sport, status) => api.get('/drills/trials', { params: { sport, status } }),
    getTrial: (trialId) => api.get(`/drills/trials/${trialId}`),
    submitToTrial: (data) => api.post('/drills/trials/submit', data),
    evaluateTrial: (trialId) => api.post(`/drills/trials/${trialId}/evaluate`),
};

// Scouting Portal API (Phase 6)
export const scoutingApi = {
    searchAthletes: (params) => api.get('/scouting/search', { params }),
    getRecommendations: (scoutId, sport) => api.get(`/scouting/recommendations/${scoutId}`, { params: sport ? { sport } : {} }),
    addToShortlist: (data) => api.post('/scouting/shortlist/add', data),
    getShortlist: (scoutId, sport) => api.get(`/scouting/shortlist/${scoutId}`, { params: sport ? { sport } : {} }),
    updateShortlist: (id, data) => api.put(`/scouting/shortlist/${id}`, data),
    removeFromShortlist: (id) => api.delete(`/scouting/shortlist/${id}`),
    compareAthletes: (athleteIds) => api.post('/scouting/compare', { athlete_ids: athleteIds }),
    getPublicProfile: (athleteId) => api.get(`/scouting/public-profile/${athleteId}`),
    getTalentPipeline: (params) => api.get('/scouting/talent-pipeline', { params }),
};


// Combined Rankings (Phase Combined)
export const combinedRankingsApi = {
    getAthletes: (params) => api.get('/combined-rankings/athletes', { params }),
    getSchools: (params) => api.get('/combined-rankings/schools', { params }),
    getConfig: () => api.get('/combined-rankings/config'),
};

// Federation Intelligence Dashboard
export const federationApi = {
    getDashboard: (params) => api.get('/federation/dashboard', { params }),
    getTalentPipeline: (sport) => api.get('/federation/talent-pipeline', { params: sport ? { sport } : {} }),
};

// Federation Real-Time Alerts (WebSocket-driven)
export const federationAlertsApi = {
    getRecent: (params) => api.get('/federation-alerts/recent', { params }),
    scan: () => api.post('/federation-alerts/scan'),
    pushTest: (params) => api.post('/federation-alerts/test', null, { params }),
    clear: () => api.delete('/federation-alerts/clear'),
};

// Live Match Intelligence (Phase 7)
export const liveIntelligenceApi = {
    listActiveMatches: () => api.get('/live-intelligence/active-matches'),
    getSession: (matchId) => api.get(`/live-intelligence/match/${matchId}/session`),
    clearSession: (matchId) => api.delete(`/live-intelligence/match/${matchId}/session`),
    pushScore: (matchId, formData) => api.post(`/live-intelligence/match/${matchId}/push-score`, formData),
    pushEvent: (matchId, formData) => api.post(`/live-intelligence/match/${matchId}/push-event`, formData),
};
